/** Dashboard de Préstamos — Seguimiento en Tiempo Real (idéntico al módulo actual). */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Encabezado, SelectUsuarios, SelectOficinas, alias } from '../componentes/Comunes';
import {
  expedientesPrestados, ultimosPrestamos, prestamosActivosDe,
  calcularRango, type RangoClave,
} from '../servicios/consultas';
import { aFecha, fmtFecha, mensajeError } from '../util';
import {
  clasificarPlazo, diasTranscurridos, COLOR_PLAZO,
  type Expediente, type Prestamo,
} from '@cji/shared';

const REFRESCO_MS = 60000;

const RANGOS: { clave: RangoClave; texto: string }[] = [
  { clave: 'hoy', texto: 'Hoy' },
  { clave: 'semana', texto: 'Esta semana' },
  { clave: 'mes', texto: 'Este mes' },
  { clave: 'personalizado', texto: 'Rango de fechas' },
];

const hoyISO = () => new Date().toISOString().slice(0, 10);

/** Días que alguien lleva con la carpeta, desde que se le asignó. */
function diasEnPoder(p: Prestamo): number {
  const f = aFecha(p.fechas?.aceptacion) ?? aFecha(p.creadoEn);
  return f ? diasTranscurridos(f) : 0;
}

export function Dashboard() {
  const [usuarioEmail, setUsuarioEmail] = useState('');
  const [oficinaId, setOficinaId] = useState('');
  const [items, setItems] = useState<Expediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ultima, setUltima] = useState<Date | null>(null);

  const [rango, setRango] = useState<RangoClave>('semana');
  const [desde, setDesde] = useState(hoyISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [recientes, setRecientes] = useState<Prestamo[]>([]);
  const [delUsuario, setDelUsuario] = useState<Prestamo[]>([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const r = calcularRango(rango, desde, hasta);
      // Al filtrar por usuario interesa TODO lo que tiene en su poder, sin
      // importar cuándo se lo prestaron; el rango aplica al listado general.
      const [exp, ult, mios] = await Promise.all([
        expedientesPrestados({ usuarioEmail: usuarioEmail || undefined, oficinaId: oficinaId || undefined }),
        ultimosPrestamos(r.desde, r.hasta),
        usuarioEmail ? prestamosActivosDe(usuarioEmail) : Promise.resolve([]),
      ]);
      setItems(exp);
      setRecientes(ult);
      setDelUsuario(mios);
      setUltima(new Date());
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setCargando(false);
    }
  }, [usuarioEmail, oficinaId, rango, desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Auto-refresco cada 60 s.
  useEffect(() => {
    const id = setInterval(() => void cargar(), REFRESCO_MS);
    return () => clearInterval(id);
  }, [cargar]);

  const stats = useMemo(() => {
    const total = items.length;
    const usuarios = new Set(items.map((x) => x.custodiaActualEmail).filter(Boolean)).size;
    let reciente = 0, normal = 0, porVencer = 0, vencido = 0;
    for (const x of items) {
      const f = aFecha(x.actualizadoEn);
      const dias = f ? diasTranscurridos(f) : 0;
      switch (clasificarPlazo(dias)) {
        case 'Reciente': reciente++; break;
        case 'Normal': normal++; break;
        case 'Por Vencer': porVencer++; break;
        case 'Vencido': vencido++; break;
      }
    }
    return { total, usuarios, reciente, normal, porVencer, vencido };
  }, [items]);

  return (
    <main className="pagina">
      <Encabezado icono="📋" titulo="Dashboard de Préstamos — Seguimiento en Tiempo Real" />

      {/* Filtros */}
      <div className="tarjeta no-imprimir">
        <div className="tarjeta-titulo">▾ Filtros de Búsqueda</div>
        <div className="grid-2">
          <div className="campo"><label>👤 Usuario</label><SelectUsuarios valor={usuarioEmail} onChange={setUsuarioEmail} placeholder="— Todos los usuarios —" /></div>
          <div className="campo"><label>🏢 Oficina</label><SelectOficinas valor={oficinaId} onChange={setOficinaId} /></div>
        </div>

        <div className="campo" style={{ marginTop: 12 }}>
          <label>📅 Periodo de los últimos préstamos</label>
          <div className="fila filtros">
            {RANGOS.map((r) => (
              <button key={r.clave} className={`btn btn-sm ${rango === r.clave ? 'btn-primario' : 'btn-secundario'}`}
                onClick={() => setRango(r.clave)}>{r.texto}</button>
            ))}
          </div>
        </div>
        {rango === 'personalizado' && (
          <div className="grid-2">
            <div className="campo"><label>Desde</label><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div className="campo"><label>Hasta</label><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
          </div>
        )}

        <div className="fila" style={{ gap: 8 }}>
          <button className="btn btn-primario" onClick={() => void cargar()}>↻ Actualizar</button>
          <button className="btn btn-secundario" onClick={() => window.print()}>⤓ Descargar PDF</button>
        </div>
      </div>

      {error && <div className="aviso error">{error}</div>}

      {/* Semáforo de plazos */}
      <div className="tarjeta" style={{ background: 'var(--azul-bg)' }}>
        <div className="tarjeta-titulo" style={{ color: 'var(--azul)' }}>ⓘ Información sobre Plazos de Préstamo</div>
        <div className="plazos">
          <div className="plazo-item" style={{ borderLeftColor: COLOR_PLAZO.Reciente, color: COLOR_PLAZO.Reciente }}>✔ Reciente: 0-10 días</div>
          <div className="plazo-item" style={{ borderLeftColor: COLOR_PLAZO.Normal, color: COLOR_PLAZO.Normal }}>◷ Normal: 11-20 días</div>
          <div className="plazo-item" style={{ borderLeftColor: COLOR_PLAZO['Por Vencer'], color: COLOR_PLAZO['Por Vencer'] }}>⚠ Por Vencer: 21-30 días</div>
          <div className="plazo-item" style={{ borderLeftColor: COLOR_PLAZO.Vencido, color: COLOR_PLAZO.Vencido }}>⊘ Vencido: Más de 30 días</div>
        </div>
        <p className="ayuda" style={{ marginTop: 10 }}>📅 Los días se calculan desde la última modificación del expediente.</p>
      </div>

      {/* KPIs */}
      <div className="kpis">
        <div className="kpi"><div className="rotulo">🗂 Total en Préstamo</div><div className="valor">{cargando ? '…' : stats.total}</div></div>
        <div className="kpi azul"><div className="rotulo">👥 Usuarios Activos</div><div className="valor">{cargando ? '…' : stats.usuarios}</div></div>
        <div className="kpi ambar"><div className="rotulo">⚠ Por Vencer <small>(21-30 días)</small></div><div className="valor">{cargando ? '…' : stats.porVencer}</div></div>
        <div className="kpi rojo"><div className="rotulo">⊘ Vencidos <small>(Más de 30 días)</small></div><div className="valor">{cargando ? '…' : stats.vencido}</div></div>
      </div>

      {/* Carpetas que tiene el usuario buscado */}
      {usuarioEmail && (
        <div className="tarjeta imprimible">
          <div className="tarjeta-titulo">
            🗃 Carpetas en poder de <b>{alias(usuarioEmail)}</b> ({delUsuario.length})
          </div>
          <TablaPrestamos filas={delUsuario} conDias vacio="Este usuario no tiene carpetas en su poder." />
        </div>
      )}

      {/* Últimos préstamos del periodo */}
      <div className="tarjeta imprimible">
        <div className="tarjeta-titulo">
          🕘 Últimos préstamos {cargando ? '' : `(${recientes.length})`}
        </div>
        <TablaPrestamos filas={recientes} vacio="No hubo préstamos en el periodo elegido." />
      </div>

      <p className="ayuda no-imprimir" style={{ textAlign: 'right', marginTop: 14 }}>
        ↻ Actualización automática cada 60 segundos{ultima ? ` · última: ${ultima.toLocaleTimeString('es-CO')}` : ''}
      </p>
    </main>
  );
}

/** Tabla de préstamos: quién la tiene, quién se la prestó y desde cuándo. */
function TablaPrestamos({ filas, conDias, vacio }: { filas: Prestamo[]; conDias?: boolean; vacio: string }) {
  if (!filas.length) return <p className="ayuda">{vacio}</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table_av">
        <thead>
          <tr>
            <th>Radicado</th><th>Préstamo</th><th>Estado</th>
            <th>La tiene</th><th>Se la prestó</th><th>Fecha</th>
            <th>{conDias ? 'Días en poder' : 'Días'}</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((p) => {
            const dias = diasEnPoder(p);
            const clase = clasificarPlazo(dias);
            return (
              <tr key={p.id}>
                <td><Link to={`/expediente/${p.expedienteId}`}><b>{p.expedienteId}</b></Link></td>
                <td>{p.radicadoHijo ?? '—'}</td>
                <td>{p.estado}</td>
                <td>{alias(p.asignadoEmail)}</td>
                <td>{alias(p.solicitanteEmail)}</td>
                <td>{fmtFecha(p.creadoEn)}</td>
                <td style={{ color: COLOR_PLAZO[clase], fontWeight: 600 }}>{dias} d</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
